import React from "react";
import { Phone } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { useUserPhoneNumbers } from "@/app/_module/hooks/usePhoneNumberPricing";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { formatPhoneNumber as formatPhoneNumberForDisplay } from "@/app/_module/utils/formatters";

interface AgentPhoneNumber {
  id: string;
  number: string;
  phone_number?: string; // Alternative format
  areaCode?: string;
  area_code?: string; // Alternative format
  state?: string; // State abbreviation (e.g., "AL")
  stateName?: string; // Full state name (e.g., "Alabama")
  assignedTo?: string;
  assignedToName?: string;
  deletedAt?: string | null;
  status?: 'active' | 'inactive';
}

interface AgentPhoneSelectorProps {
  /** Currently selected phone number */
  selectedPhoneNumber?: string;
  /** Callback when phone number is selected */
  onPhoneNumberSelect?: (phoneNumber: string, phoneData: AgentPhoneNumber) => void;
  /** Show only specific phone number (useful for read-only display) */
  displayOnly?: boolean;
  /** Custom placeholder text */
  placeholder?: string;
  /** Size variant */
  size?: 'sm' | 'default' | 'lg';
  /** Show area code badge */
  showAreaCode?: boolean;
  /** Show state inline with phone number instead of area code badge */
  showStateInline?: boolean;
  /** Filter out deleted/inactive numbers */
  activeOnly?: boolean;
  /** Custom trigger button text */
  triggerText?: string;
  /** Show phone icon in trigger */
  showIcon?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Auto-select user's default outbound phone on load */
  autoSelectDefault?: boolean;
}

export function AgentPhoneSelector({
  selectedPhoneNumber,
  onPhoneNumberSelect,
  displayOnly = false,
  placeholder = "Select phone number",
  size = 'default',
  showAreaCode = true,
  showStateInline = false,
  activeOnly = true,
  triggerText,
  showIcon = true,
  disabled = false,
  autoSelectDefault = false,
}: AgentPhoneSelectorProps) {
  const { data: phoneNumbers = [], isLoading, error } = useUserPhoneNumbers();
  const { user } = useUserContext();
  
  // Get user's default outbound phone from profile
  const userDefaultPhone = (user?.user as unknown as Record<string, unknown>)?.defaultOutboundPhone as string;

  // Filter and normalize phone numbers
  const availablePhoneNumbers: AgentPhoneNumber[] = React.useMemo(() => {
    if (!Array.isArray(phoneNumbers)) return [];

    return phoneNumbers
      .map((phone: any) => {
        const rawState = phone.state || phone.State || '';

        return {
          id: phone.id || phone.phoneId || String(Math.random()),
          number: phone.number || phone.phone_number || phone.phoneNumber || '',
          phone_number: phone.phone_number || phone.phoneNumber,
          areaCode: phone.areaCode || phone.area_code,
          area_code: phone.area_code,
          state: rawState,
          stateName: rawState,
          assignedTo: phone.assignedTo,
          assignedToName: phone.assignedToName,
          deletedAt: phone.deletedAt,
          status: phone.status || (!phone.deletedAt ? 'active' : 'inactive'),
        };
      })
      .filter((phone: AgentPhoneNumber) => {
        // Filter out numbers without a valid phone number
        if (!phone.number && !phone.phone_number) return false;
        
        // Filter out deleted/inactive numbers if activeOnly is true
        if (activeOnly && (phone.deletedAt || phone.status === 'inactive')) return false;
        
        return true;
      });
  }, [phoneNumbers, activeOnly]);

  // Find currently selected phone data
  const selectedPhoneData = React.useMemo(() => {
    if (!selectedPhoneNumber) return null;
    return availablePhoneNumbers.find(
      phone => phone.number === selectedPhoneNumber || phone.phone_number === selectedPhoneNumber
    );
  }, [selectedPhoneNumber, availablePhoneNumbers]);

  // Format phone number for display
  const formatPhoneNumber = React.useCallback((phoneNumber: string) => {
    try {
      return formatPhoneNumberForDisplay(phoneNumber);
    } catch {
      // Fallback formatting if utility fails
      const cleaned = phoneNumber.replace(/\D/g, '');
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      }
      return phoneNumber;
    }
  }, []);

  // Handle phone number selection
  const handlePhoneSelect = React.useCallback((phone: AgentPhoneNumber) => {
    const phoneNumber = phone.number || phone.phone_number || '';
    if (onPhoneNumberSelect && phoneNumber) {
      onPhoneNumberSelect(phoneNumber, phone);
    }
  }, [onPhoneNumberSelect]);

  // Auto-select user's default phone if enabled and no phone is currently selected
  // Falls back to first available number if no default is set
  React.useEffect(() => {
    if (
      autoSelectDefault &&
      !selectedPhoneNumber &&
      availablePhoneNumbers.length > 0 &&
      onPhoneNumberSelect
    ) {
      // Try user's default phone first
      if (userDefaultPhone) {
        const defaultPhoneData = availablePhoneNumbers.find(
          phone => phone.number === userDefaultPhone || phone.phone_number === userDefaultPhone
        );
        if (defaultPhoneData) {
          handlePhoneSelect(defaultPhoneData);
          return;
        }
      }
      // Fallback: auto-select first available number
      handlePhoneSelect(availablePhoneNumbers[0]);
    }
  }, [autoSelectDefault, userDefaultPhone, selectedPhoneNumber, availablePhoneNumbers, onPhoneNumberSelect, handlePhoneSelect]);

  // Display loading state
  if (isLoading) {
    return (
      <Select disabled>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Loading..." />
        </SelectTrigger>
      </Select>
    );
  }

  // Display error state
  if (error) {
    return (
      <Select disabled>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Error loading numbers" />
        </SelectTrigger>
      </Select>
    );
  }

  // Display only mode (no dropdown)
  if (displayOnly) {
    if (!selectedPhoneData) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Phone className="h-4 w-4" />
          No phone number selected
        </div>
      );
    }

    const phoneNumber = selectedPhoneData.number || selectedPhoneData.phone_number || '';
    return (
      <div className="flex items-center gap-2">
        <span className="font-medium">{formatPhoneNumber(phoneNumber)}</span>
        {showStateInline && selectedPhoneData.state && (
          <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">
            {selectedPhoneData.state}
          </span>
        )}
      </div>
    );
  }

  // No phone numbers available
  if (availablePhoneNumbers.length === 0) {
    return (
      <Select disabled>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="No phone numbers available" />
        </SelectTrigger>
      </Select>
    );
  }

  return (
    <Select
      value={selectedPhoneNumber || ""}
      onValueChange={(value) => {
        const phone = availablePhoneNumbers.find(
          p => (p.number || p.phone_number) === value
        );
        if (phone) {
          handlePhoneSelect(phone);
        }
      }}
      disabled={disabled}
    >
      <SelectTrigger className="w-full">
        {selectedPhoneData ? (
          <div className="flex items-center gap-2 flex-1">
            <span>{formatPhoneNumber(selectedPhoneData.number || selectedPhoneData.phone_number || '')}</span>
            {showStateInline && selectedPhoneData.state && (
              <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">
                {selectedPhoneData.state}
              </span>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
      </SelectTrigger>
      <SelectContent>
        {availablePhoneNumbers.map((phone) => {
          const phoneNumber = phone.number || phone.phone_number || '';

          return (
            <SelectItem key={phone.id} value={phoneNumber}>
              <div className="flex items-center justify-between w-full gap-2">
                <span>{formatPhoneNumber(phoneNumber)}</span>
                {showStateInline && phone.stateName && (
                  <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">
                    {phone.stateName}
                  </span>
                )}
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

export default AgentPhoneSelector;